// The following module serves as an entrypoint for the usage of all 3rd party
// integrations. Any components or libraries should always make use of this
// module instead of interfacing with 3rd party libraries directly. Doing so
// allows our frontend code to remain integration-agnostic.
import config from "@/config";
import { get as lodashGet } from "lodash";

// Checks whether the application that loads this module has a specific
// integration configured. Also checks whether all variables required to
// enable the specific integration are present.
function isConfigured(
    integrationType: string,
    integrationName: string,
    locators: string[]
) {
    const integrationConfig = lodashGet(
        config,
        `${integrationType}.${integrationName}`
    );
    // When there is no configuration specified for the the given integration,
    // there is nothing to do.
    if (!integrationConfig) {
        return false;
    }

    // Note, we're returning early through 'some'. As soon as it returns
    // 'true', a value is misconfigured for a certain integration.
    return !locators.some((locator) => {
        const value = lodashGet(integrationConfig, locator);
        // When "%%" exist in the string, either the application is in 'development'
        // mode, in which case there is no need to configure certain integrations
        // or an environment variable hasn't been injected properly.
        if (value.includes("%%")) {
            console.log(
                `Not configuring "${integrationType}.${integrationName}", "${locator}" hasn't been updated. (${value})`
            );
            return true;
        }

        if (!value) {
            console.log(
                `Not configuring "${integrationType}.${integrationName}", "${locator}" hasn't been set.`
            );
            return true;
        }

        return false;
    });
}

const isSegmentConfigured = isConfigured("analytics", "segment", ["writeKey"]);
const isMixPanelConfigured = isConfigured("analytics", "mixpanel", [
    "projectToken",
]);

// The following is used as a reference to check whether a specific service is
// configured. If so, it can be enabled for this application.
const _configured = {
    analytics: {
        segment: isSegmentConfigured,
        mixpanel: isMixPanelConfigured,
    },
};

// The following functions are empty, so that they that can still be called
// even though 3rd party analytics have not been initialized for this
// particular app.
// However it still proxies the types in case the integrations are activated
// for the given project.
const analyticsLibraries = {
    segment: {
        page: (() => undefined) as unknown,
        track: (() => undefined) as unknown,
    },
    mixpanel: {
        track: (() => undefined) as unknown,
    },
};

function page() {
    analyticsLibraries.segment.page();
    analyticsLibraries.mixpanel.track("Viewed");
}

// The following allows us to use multiple analytics integrations at the same
// time if needed. If they are not configured, the empty function are called
// instead.
function track(
    name: string,
    options?: { [extraInfo: string]: string | number }
) {
    analyticsLibraries.segment.track(name, options);
    analyticsLibraries.mixpanel.track(name, options);
}

export const analytics = {
    page,
    track,
};

function error(description: string, _error?: Error) {
    console.error(description);
}

// Not logging these anywhere yet, just keeping this function as a placeholder
// so we can integrate it with a logging library or service later.
function warning(_description: string) {}

export const monitoring = {
    error,
    warning,
};
